import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import { useNavigate } from "react-router-dom";
import { FormSection, FormSection2, FormGroup } from "../components/FormItems";
import nationalitiesData from "../data/Nationalities.json";
import depts_unitsData from "../data/eacDepartments.json";
import { LoadingButton } from "../components/loadingButton";
import apiClient from "../api/axiosInstance";

const ApplicationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // State variables for input fields
  const [surname, setSurname] = useState("");
  const [firstName, setFirstName] = useState("");
  const [otherNames, setOtherNames] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [nationality, setNationality] = useState("");
  const [idPassportNumber, setIdPassportNumber] = useState("");
  const [address, setAddress] = useState("");
  const [identificationDocument, setIdentificationDocument] = useState(null);
  const [institutionName, setInstitutionName] = useState("");
  const [courseProgram, setCourseProgram] = useState("");
  const [currentYear, setCurrentYear] = useState("");
  const [yearOfGraduation, setYearOfGraduation] = useState("");
  const [academicQualification, setAcademicQualification] = useState("");
  const [academicDocuments, setAcademicDocuments] = useState(null);
  const [insurancePolicyNumber, setInsurancePolicyNumber] = useState("");
  const [insuranceCompany, setInsuranceCompany] = useState("");
  const [policyExpirationDate, setPolicyExpirationDate] = useState("");
  const [emergencyContactPerson, setEmergencyContactPerson] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");
  const [emergencyContactEmail, setEmergencyContactEmail] = useState("");
  const [insuranceDocument, setInsuranceDocument] = useState(null);
  const [internshipDepartment, setInternshipDepartment] = useState("");
  const [internshipStartDate, setInternshipStartDate] = useState("");
  const [internshipEndDate, setInternshipEndDate] = useState("");

  // State variables for error handling
  const [errors, setErrors] = useState({});

  const [checkbox1Checked, setCheckbox1Checked] = useState(false);
  const [checkbox2Checked, setCheckbox2Checked] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isGraduated, setIsGraduated] = useState(false);

  const educationAwards = [
    "Master's Degree",
    "Bachelor's Degree",
    "Diploma",
    "Higher Diploma",
  ];

  const nationalityOptions = [
    "Select Nationality",
    ...nationalitiesData.nationalities.map((nat) => nat.nationality),
  ];

  const depts_units = ["Choose a Department/Unit", ...depts_unitsData.units];

  // Input change handler - to set values and clear errors on input value change
  const handleInputChange = (setter, fieldName) => (e) => {
    setter(e.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: undefined, // Clearing the error for the specific field
    }));
  };

  const handleGraduationCheckboxChange = (e) => {
    setIsGraduated(e.target.checked);
    if (isGraduated) {
      setCurrentYear("Graduated");
    }
    // If unchecked, clear Year of Graduation and Academic Award fields
    if (!e.target.checked) {
      setYearOfGraduation("");
      setAcademicQualification("");
    }
  };

  const handleCurrentYearChange = (e) => {
    setCurrentYear(e.target.value);
  };

  // Handle checkbox change for both checkboxes
  const handleCheckboxChange = (checkboxIndex) => {
    if (checkboxIndex === 1) {
      setCheckbox1Checked(!checkbox1Checked);
    } else if (checkboxIndex === 2) {
      setCheckbox2Checked(!checkbox2Checked);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!surname) newErrors.surname = "Surname is required.";
    if (!firstName) newErrors.firstName = "First name is required.";
    if (!email) newErrors.email = "Email is required.";
    if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      newErrors.email = "Email is invalid.";
    }
    if (!phoneNumber) newErrors.phoneNumber = "Phone number is required.";
    if (!nationality || nationality === "Select Nationality")
      newErrors.nationality = "Nationality is required.";
    if (!idPassportNumber)
      newErrors.idPassportNumber = "ID/Passport Number is required.";
    if (!institutionName)
      newErrors.institutionName = "Institution Name is required.";
    if (!courseProgram) newErrors.courseProgram = "Course/Program is required.";
    if (!insurancePolicyNumber)
      newErrors.insurancePolicyNumber = "Insurance Policy Number is required.";
    if (!policyExpirationDate)
      newErrors.policyExpirationDate = "Policy Expiration Date is required.";
    if (!insuranceCompany)
      newErrors.insuranceCompany = "Insurance Company is required.";
    if (
      !internshipDepartment ||
      internshipDepartment === "Choose a Department/Unit"
    ) {
      newErrors.internshipDepartment = "Department/Unit is required.";
    }
    if (!internshipStartDate)
      newErrors.internshipStartDate = "Start Date is required.";
    if (!internshipEndDate)
      newErrors.internshipEndDate = "End Date is required.";

    // Validate file uploads
    if (!identificationDocument)
      newErrors.identificationDocument = "Identification Document is required.";
    if (!academicDocuments)
      newErrors.academicDocuments = "Academic Documents are required.";
    if (!insuranceDocument)
      newErrors.insuranceDocument = "Insurance Document is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };

  const handleApplicationFormSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) return;

    // Create a new FormData object
    const formData = new FormData();

    // Append form fields to the FormData object
    formData.append("surname", surname);
    formData.append("firstName", firstName);
    formData.append("otherNames", otherNames);
    formData.append("email", email);
    formData.append("phoneNumber", phoneNumber);
    formData.append("nationality", nationality);
    formData.append("idPassportNumber", idPassportNumber);
    formData.append("address", address);
    formData.append("institutionName", institutionName);
    formData.append("courseProgram", courseProgram);
    formData.append("currentYear", currentYear);
    formData.append("yearOfGraduation", yearOfGraduation);
    formData.append("academicQualification", academicQualification);
    formData.append("insurancePolicyNumber", insurancePolicyNumber);
    formData.append("insuranceCompany", insuranceCompany);
    formData.append("policyExpirationDate", policyExpirationDate);
    formData.append("emergencyContactPerson", emergencyContactPerson);
    formData.append("emergencyContactPhone", emergencyContactPhone);
    formData.append("emergencyContactEmail", emergencyContactEmail);
    formData.append("internshipDepartment", internshipDepartment);
    formData.append("internshipStartDate", internshipStartDate);
    formData.append("internshipEndDate", internshipEndDate);

    // Append file fields
    formData.append("identificationDocument", identificationDocument);
    formData.append("academicDocuments", academicDocuments);
    formData.append("insuranceDocument", insuranceDocument);

    setLoading(true);

    try {
      // Send POST request with FormData
      const response = await apiClient.post("/api/apply-internship", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Handle success
      console.log(response.data);
      navigate("/submission-success");
    } catch (error) {
      // Handle errors
      console.error("Error submitting application:", error);
      alert(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Update the 'agreed' state based on checkboxes
  useEffect(() => {
    setAgreed(checkbox1Checked && checkbox2Checked);
  }, [checkbox1Checked, checkbox2Checked]);

  return (
    <div>
      <Header />
      <div className="bg-gray-100 pb-10 sticky top-[7.5rem] z-40 h-[calc(100vh-7.5rem)] overflow-y-auto">
        {/* Main Content */}
        <main className="shadow-md bg-white max-w-screen-xl p-4 md:px-20 w-full mx-auto">
          <h1 className="text-2xl font-bold text-gray-600 text-center mb-4">
            Internship Application Form
          </h1>
          <p className="text-gray-600 text-center mb-8">
            Please fill in all the fields below with your details and submit to
            complete the internship application.
          </p>
          <div className="bg-gray-100 p-5 md:p-8 border-2 border-green-600 rounded-lg mb-10">
            <form onSubmit={handleApplicationFormSubmit}>
              <FormSection title="Personal Details">
                <FormGroup
                  id="surname"
                  label="Surname"
                  name="surname"
                  type="text"
                  value={surname}
                  onChange={handleInputChange(setSurname, "surname")}
                  className={`border p-2 font-medium ${
                    errors.surname ? "border-red-500" : "border-gray-300"
                  }`}
                  errorMessage={errors.surname}
                />
                <FormGroup
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  type="text"
                  value={firstName}
                  onChange={handleInputChange(setFirstName, "firstName")}
                  className={`border p-2 font-medium ${
                    errors.firstName ? "border-red-500" : "border-gray-300"
                  }`}
                  errorMessage={errors.firstName}
                />
                <FormGroup
                  id="otherNames"
                  label="Other Names"
                  name="otherNames"
                  type="text"
                  value={otherNames}
                  onChange={(e) => setOtherNames(e.target.value)}
                  className="border p-2 font-medium "
                  errorMessage={errors.otherNames}
                />
                <FormGroup
                  id="email"
                  label="Email Address"
                  name="email"
                  type="text"
                  value={email}
                  onChange={handleInputChange(setEmail, "email")}
                  className={`border p-2 font-medium ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  errorMessage={errors.email}
                />
                <FormGroup
                  id="phoneNumber"
                  label="Phone Number"
                  name="phoneNumber"
                  type="text"
                  value={phoneNumber}
                  onChange={handleInputChange(setPhoneNumber, "phoneNumber")}
                  className={`border p-2 font-medium ${
                    errors.phoneNumber ? "border-red-500" : "border-gray-300"
                  }`}
                  errorMessage={errors.phoneNumber}
                />
                <FormGroup
                  id="nationality"
                  label="Nationality"
                  name="nationality"
                  value={nationality}
                  onChange={handleInputChange(setNationality, "nationality")}
                  className={`border p-2 font-medium ${
                    errors.nationality ? "border-red-500" : "border-gray-300"
                  }`}
                  errorMessage={errors.nationality}
                  type="select"
                  options={nationalityOptions}
                />
                <FormGroup
                  id="idPassportNumber"
                  label="National ID/Passport Number"
                  name="idPassportNumber"
                  type="text"
                  value={idPassportNumber}
                  className={`border p-2 font-medium ${
                    errors.idPassportNumber
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.idPassportNumber}
                  onChange={handleInputChange(
                    setIdPassportNumber,
                    "idPassportNumber"
                  )}
                />
                <FormGroup
                  id="address"
                  label="Address"
                  name="address"
                  type="text"
                  value={address}
                  className="border p-2 font-medium"
                  onChange={(e) => {
                    setAddress(e.target.value);
                  }}
                />

                {/* Identification Document */}
                <div className="flex flex-col md:col-span-2 lg:col-span-3">
                  <label
                    htmlFor="Upload Document"
                    className="mb-2 text-sm md:text-base font-medium text-gray-700"
                  >
                    Upload Identification Document
                    <p className="text-xs md:text-sm text-red-700">
                      (Allowed file types: .pdf, .docx, .doc Max: 2MB)
                    </p>
                  </label>

                  <input
                    id="identificationDocument"
                    type="file"
                    className={`p-3 border border-gray-300 font-medium text-gray-500 rounded-md ${
                      errors.identificationDocument
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      setIdentificationDocument(e.target.files[0]); // Set the selected file
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        identificationDocument: undefined, // Clear the error for the specific field
                      }));
                    }}
                  />
                  {errors.identificationDocument && (
                    <span className="text-red-500 text-xs md:text-sm">
                      {errors.identificationDocument}
                    </span>
                  )}
                </div>
              </FormSection>

              {/*Education Section*/}
              <FormSection title="Education">
                <FormGroup
                  id="institutionName"
                  label="Institution Name"
                  name="institutionName"
                  type="text"
                  value={institutionName}
                  className={`border p-2 font-medium ${
                    errors.institutionName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.institutionName}
                  onChange={handleInputChange(
                    setInstitutionName,
                    "institutionName"
                  )}
                />
                <FormGroup
                  id="courseProgram"
                  label="Course / Program"
                  name="courseProgram"
                  type="text"
                  value={courseProgram}
                  className={`border p-2 font-medium ${
                    errors.courseProgram ? "border-red-500" : "border-gray-300"
                  }`}
                  errorMessage={errors.courseProgram}
                  onChange={handleInputChange(
                    setCourseProgram,
                    "courseProgram"
                  )}
                />

                <FormGroup
                  label="Graduated"
                  type="checkbox"
                  checked={isGraduated}
                  onChange={handleGraduationCheckboxChange}
                />

                <FormGroup
                  id="currentYear"
                  name="currentYear"
                  label="Current School Year"
                  type="select"
                  options={[
                    "Year 1",
                    "Year 2",
                    "Year 3",
                    "Year 4",
                    "Year 5",
                    "Year 6",
                  ]}
                  value={currentYear}
                  onChange={handleCurrentYearChange}
                  disabled={isGraduated}
                  className="font-medium"
                />
                <FormGroup
                  id="yearOfGraduation"
                  name="yearOfGraduation"
                  className="font-medium"
                  label="Year of Graduation"
                  type="select"
                  options={Array.from(
                    { length: 3 },
                    (_, i) => new Date().getFullYear() - i
                  )}
                  disabled={!isGraduated} // Enable if graduated
                  value={yearOfGraduation}
                  onChange={(e) => setYearOfGraduation(e.target.value)}
                />
                <FormGroup
                  id="academicQualification"
                  name="academicQualification"
                  label="Academic Qualification Awarded"
                  type="select"
                  options={educationAwards}
                  value={academicQualification}
                  className="font-medium"
                  onChange={(e) => setAcademicQualification(e.target.value)}
                  disabled={!isGraduated}
                />

                {/* Academic Documents */}
                <div className="flex flex-col md:col-span-2 lg:col-span-3">
                  <label
                    htmlFor="Upload Academic Document"
                    className="mb-2 text-sm md:text-base font-medium text-gray-700"
                  >
                    Upload Academic Documents / Letter of Introduction
                    <p className="text-xs md:text-sm text-red-700">
                      (Allowed file types: .pdf, .docx, .doc Max: 2MB)
                    </p>
                  </label>

                  <input
                    id="academicDocuments"
                    type="file"
                    className={`p-3 border border-gray-300 font-medium text-gray-500 rounded-md ${
                      errors.academicDocuments
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      setAcademicDocuments(e.target.files[0]);
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        academicDocuments: undefined,
                      }));
                    }}
                    s
                  />
                  {errors.academicDocuments && (
                    <span className="text-red-500 text-xs md:text-sm">
                      {errors.academicDocuments}
                    </span>
                  )}
                </div>
              </FormSection>

              {/* Medical Insurance & Emergency Section */}
              <FormSection title="Medical Insurance & Emergency Details">
                <FormGroup
                  id="insurancePolicyNumber"
                  label="Insurance Policy Number"
                  name="insurancePolicyNumber"
                  type="text"
                  value={insurancePolicyNumber}
                  className={`border p-2 font-medium ${
                    errors.insurancePolicyNumber
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.insurancePolicyNumber}
                  onChange={handleInputChange(
                    setInsurancePolicyNumber,
                    "insurancePolicyNumber"
                  )}
                />
                <FormGroup
                  id="insuranceCompany"
                  label="Insurance Company"
                  name="insuranceCompany"
                  type="text"
                  value={insuranceCompany}
                  className={`border p-2 font-medium ${
                    errors.insuranceCompany
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.insuranceCompany}
                  onChange={handleInputChange(
                    setInsuranceCompany,
                    "insuranceCompany"
                  )}
                />
                <FormGroup
                  label="Policy Expiration Date"
                  type="date"
                  value={policyExpirationDate}
                  onChange={handleInputChange(
                    setPolicyExpirationDate,
                    "policyExpirationDate"
                  )}
                  className={`border p-2 font-medium ${
                    errors.policyExpirationDate
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.policyExpirationDate}
                />
                <FormGroup
                  label="Emergency Contact Person"
                  type="text"
                  value={emergencyContactPerson}
                  className="font-medium"
                  onChange={(e) => setEmergencyContactPerson(e.target.value)}
                />
                <FormGroup
                  label="Emergency Phone Number"
                  type="tel"
                  value={emergencyContactPhone}
                  className="font-medium"
                  onChange={(e) => setEmergencyContactPhone(e.target.value)}
                />
                <FormGroup
                  label="Emergency Email Address"
                  type="email"
                  value={emergencyContactEmail}
                  className="font-medium"
                  onChange={(e) => setEmergencyContactEmail(e.target.value)}
                />

                {/* Insurance document */}
                <div className="flex flex-col md:col-span-2 lg:col-span-3">
                  <label
                    htmlFor="Upload Insurance Document"
                    className="mb-2 text-sm md:text-base font-medium text-gray-700"
                  >
                    Upload Insurance Policy Document / Card
                    <p className="text-xs md:text-sm text-red-700">
                      (Allowed file types: .pdf, .docx, .doc Max: 2MB)
                    </p>
                  </label>

                  <input
                    id="insuranceDocument"
                    type="file"
                    className={`p-3 border border-gray-300 font-medium text-gray-500 rounded-md ${
                      errors.insuranceDocument
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      setInsuranceDocument(e.target.files[0]);
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        insuranceDocument: undefined,
                      }));
                    }}
                  />
                  {errors.insuranceDocument && (
                    <span className="text-red-500 text-xs md:text-sm">
                      {errors.insuranceDocument}
                    </span>
                  )}
                </div>
              </FormSection>

              {/*Application Section*/}
              <FormSection title="Application Details">
                <FormGroup
                  id="internshipDepartment"
                  name="internshipDepartment"
                  label="Preferred Department/Unit"
                  type="select"
                  options={depts_units}
                  value={internshipDepartment}
                  onChange={handleInputChange(
                    setInternshipDepartment,
                    "internshipDepartment"
                  )}
                  className={`border p-2 font-medium ${
                    errors.internshipDepartment
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.internshipDepartment}
                />
                <FormGroup
                  id="internshipStartDate"
                  name="internshipStartDate"
                  label="Internship Start Date"
                  type="date"
                  value={internshipStartDate}
                  onChange={handleInputChange(
                    setInternshipStartDate,
                    "internshipStartDate"
                  )}
                  className={`border p-2 font-medium ${
                    errors.internshipStartDate
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.internshipStartDate}
                />
                <FormGroup
                  id="internshipEndDate"
                  name="internshipEndDate"
                  label="Internship End Date"
                  type="date"
                  value={internshipEndDate}
                  onChange={handleInputChange(
                    setInternshipEndDate,
                    "internshipEndDate"
                  )}
                  className={`border p-2 font-medium ${
                    errors.internshipEndDate
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  errorMessage={errors.internshipEndDate}
                />
              </FormSection>

              {/*Agreement Checkboxes*/}
              <FormSection2 title="Confirmation & Declaration">
                <div className="flex flex-col space-y-4 ">
                  {/* First Checkbox for Privacy Policy and Terms of Submission */}
                  <div className="flex">
                    <input
                      type="checkbox"
                      id="agreePrivacyPolicy"
                      onChange={() => handleCheckboxChange(1)}
                      checked={checkbox1Checked}
                      className="w-[17px]"
                    />
                    <label
                      htmlFor="agreePrivacyPolicy"
                      className="ml-4 text-sm md:text-base font-bold text-gray-600"
                    >
                      I agree to the{" "}
                      <a
                        href="https://www.eac.int/privacy"
                        className="text-blue-600 hover:underline"
                      >
                        privacy policy
                      </a>{" "}
                      and terms of submission with regards to the information
                      provided above.
                    </label>
                  </div>

                  {/* Second Checkbox for Declaration of Information Accuracy */}
                  <div className="flex">
                    <input
                      type="checkbox"
                      id="declareAccuracy"
                      onChange={() => handleCheckboxChange(2)}
                      checked={checkbox2Checked}
                      className="w-[17px]"
                    />
                    <label
                      htmlFor="declareAccuracy"
                      className="ml-4 text-sm md:text-base font-bold text- text-gray-600"
                    >
                      I declare that all the information provided is accurate
                      and truthful to the best of my knowledge and belief.
                    </label>
                  </div>
                </div>
              </FormSection2>
              <LoadingButton
                disabled={!agreed}
                type="submit"
                buttonText="Submit Application"
                loadingText="Submitting Application...."
                isLoading={loading}
                className="w-3/6 mt-6 px-[5px] py-[12px] text-sm md:text-base md:p-[11px] text-center font-semibold text-white bg-green-700 rounded-[12px] md:rounded-[15px] hover:bg-green-800  mx-auto block"
              />
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ApplicationPage;
